import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { imageUrl } from '../lib/images';

export default function AlbumModal({
  isOpen,
  album,
  initialIndex = 0,
  onClose,
  onPrevAlbum,
  onNextAlbum,
  prevAlbumTitle,
  nextAlbumTitle
}) {
  const [activeIdx, setActiveIdx] = useState(initialIndex);
  const [viewMode, setViewMode] = useState('carousel'); // 'carousel' | 'grid'
  const thumbnailsRef = useRef(null);
  const activeThumbRef = useRef(null);

  // Touch handling til mobil swipe
  const touchStartXRef = useRef(0);
  const touchEndXRef = useRef(0);

  // Nulstil aktivt billede når et nyt album vælges
  useEffect(() => {
    setActiveIdx(initialIndex);
    setViewMode('carousel');
  }, [album?.id, initialIndex]);

  const images = album?.images && album.images.length > 0
    ? album.images
    : album?.url
    ? [album.url]
    : [];

  const totalImages = images.length;

  // Lås scroll på body under modal
  useEffect(() => {
    if (!isOpen) {
      if (document.body.style.overflow === 'hidden') {
        document.body.style.overflow = '';
      }
      return;
    }
    const originalOverflow = document.body.style.overflow;
    const safeOriginal = originalOverflow === 'hidden' ? '' : originalOverflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = safeOriginal;
    };
  }, [isOpen]);

  // Tastaturnavigation (Escape til at lukke, piletaster til at bladre)
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      } else if (e.key === 'ArrowRight') {
        if (totalImages > 1) {
          setActiveIdx((prev) => (prev === totalImages - 1 ? 0 : prev + 1));
        }
      } else if (e.key === 'ArrowLeft') {
        if (totalImages > 1) {
          setActiveIdx((prev) => (prev === 0 ? totalImages - 1 : prev - 1));
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, totalImages, onClose]);

  // Scroll det aktive miniaturebillede i fokus
  useEffect(() => {
    if (activeThumbRef.current && thumbnailsRef.current) {
      activeThumbRef.current.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest'
      });
    }
  }, [activeIdx, isOpen, viewMode]);

  if (!isOpen || !album || images.length === 0) {
    return null;
  }

  const handlePrev = () => {
    if (totalImages <= 1) return;
    setActiveIdx((prev) => (prev === 0 ? totalImages - 1 : prev - 1));
  };

  const handleNext = () => {
    if (totalImages <= 1) return;
    setActiveIdx((prev) => (prev === totalImages - 1 ? 0 : prev + 1));
  };

  const handleTouchStart = (e) => {
    touchStartXRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    touchEndXRef.current = e.changedTouches[0].clientX;
    const diffX = touchStartXRef.current - touchEndXRef.current;
    if (diffX > 50) {
      handleNext();
    } else if (diffX < -50) {
      handlePrev();
    }
  };

  const currentImageUrl = images[activeIdx] || images[0];

  return createPortal(
    <div
      className="fixed inset-0 z-[99999] flex flex-col justify-between bg-black/95 backdrop-blur-md text-white select-none transition-opacity duration-300"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={album.title || 'Album visning'}
    >
      {/* 1. HEADER */}
      <header
        className="relative z-50 flex items-center justify-between border-b border-white/10 bg-black/60 px-4 py-3 sm:px-6 sm:py-4 backdrop-blur-sm"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Album info */}
        <div className="min-w-0 pr-4">
          <div className="flex items-center gap-2.5 flex-wrap">
            <span className="inline-flex items-center gap-1.5 rounded-md bg-[#325d52] px-2.5 py-0.5 text-xs font-semibold text-[#deecd4] border border-[#deecd4]/20 shadow-xs">
              <i className="fa-solid fa-layer-group text-[10px]" />
              <span>{totalImages} {totalImages === 1 ? 'billede' : 'billeder'} i albummet</span>
            </span>

            {album.category && (
              <span className="text-xs text-gray-400 hidden sm:inline-block">
                • {album.category}
              </span>
            )}
          </div>

          <h3 className="text-lg sm:text-xl font-bold text-white tracking-tight truncate mt-1">
            {album.title || 'Projektalbum'}
          </h3>

          {album.description && (
            <p className="text-xs sm:text-sm text-gray-300 truncate max-w-2xl mt-0.5">
              {album.description}
            </p>
          )}
        </div>

        {/* Funktionsknapper i højre side */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {totalImages > 1 && (
            <button
              onClick={() => setViewMode((m) => (m === 'carousel' ? 'grid' : 'carousel'))}
              className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs sm:text-sm font-semibold transition-all cursor-pointer border ${
                viewMode === 'grid'
                  ? 'bg-[#deecd4] text-[#1b453f] border-[#deecd4] shadow-sm'
                  : 'bg-white/10 text-white border-white/15 hover:bg-white/20'
              }`}
              title={viewMode === 'grid' ? 'Skift til billed-slider' : 'Se alle billeder i gitter'}
            >
              <i className={viewMode === 'grid' ? 'fa-solid fa-images' : 'fa-solid fa-table-cells-large'} />
              <span className="hidden sm:inline">
                {viewMode === 'grid' ? 'Enkeltvisning' : 'Vis alle billeder'}
              </span>
            </button>
          )}

          {/* Luk-knap */}
          <button
            onClick={onClose}
            className="flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-xl bg-white/15 text-white hover:bg-white/25 active:scale-95 transition-all border border-white/20 shadow-lg cursor-pointer"
            aria-label="Luk album"
          >
            <i className="fa-solid fa-xmark text-lg" />
          </button>
        </div>
      </header>

      {/* 2. HOVEDINDHOLD: ENTEN SLIDER/CAROUSEL ELLER GITTER */}
      {viewMode === 'carousel' ? (
        <div
          className="relative flex-1 flex items-center justify-center p-3 sm:p-6 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Forrige billede knap */}
          {totalImages > 1 && (
            <button
              onClick={handlePrev}
              className="absolute left-2 sm:left-6 z-40 flex h-11 w-11 sm:h-14 sm:w-14 items-center justify-center rounded-2xl bg-black/60 text-white backdrop-blur-md border border-white/20 hover:bg-white/20 hover:border-white/40 active:scale-95 transition-all cursor-pointer shadow-2xl"
              aria-label="Forrige billede"
            >
              <i className="fa-solid fa-chevron-left text-lg sm:text-xl" />
            </button>
          )}

          {/* Hovedbillede */}
          <div className="relative max-h-[70vh] sm:max-h-[72vh] max-w-full flex flex-col items-center justify-center">
            <img
              src={imageUrl(currentImageUrl, 'width=1600,quality=90,format=auto')}
              alt={`${album.title || 'Projekt'} - Billede ${activeIdx + 1} af ${totalImages}`}
              className="max-h-[66vh] sm:max-h-[70vh] max-w-[94vw] sm:max-w-[85vw] object-contain rounded-2xl shadow-2xl transition-all duration-300 select-none"
              loading="eager"
            />

            {/* Billede tæller indikator pill */}
            {totalImages > 1 && (
              <div className="absolute top-3 right-3 sm:top-4 sm:right-4 z-20">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-black/70 px-3 py-1 text-xs font-semibold text-white backdrop-blur-md border border-white/20 shadow-md">
                  <span className="text-[#deecd4] font-bold">{activeIdx + 1}</span>
                  <span className="text-gray-400">/</span>
                  <span>{totalImages}</span>
                </span>
              </div>
            )}
          </div>

          {/* Næste billede knap */}
          {totalImages > 1 && (
            <button
              onClick={handleNext}
              className="absolute right-2 sm:right-6 z-40 flex h-11 w-11 sm:h-14 sm:w-14 items-center justify-center rounded-2xl bg-black/60 text-white backdrop-blur-md border border-white/20 hover:bg-white/20 hover:border-white/40 active:scale-95 transition-all cursor-pointer shadow-2xl"
              aria-label="Næste billede"
            >
              <i className="fa-solid fa-chevron-right text-lg sm:text-xl" />
            </button>
          )}
        </div>
      ) : (
        /* GITTERVISNING: ALLE BILLEDER I ALBUMMET KAN SCROLLES OG OVERBLIKKES */
        <div
          className="flex-1 overflow-y-auto p-4 sm:p-8"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="max-w-6xl mx-auto space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-white/10">
              <p className="text-sm text-gray-300">
                Klik på et billede for at åbne det i stor visning:
              </p>
              <span className="text-xs font-semibold text-[#deecd4]">
                {totalImages} billeder i {album.title}
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
              {images.map((imgUrl, idx) => {
                const isActive = idx === activeIdx;
                return (
                  <div
                    key={`${imgUrl}-${idx}`}
                    onClick={() => {
                      setActiveIdx(idx);
                      setViewMode('carousel');
                    }}
                    className={`group relative aspect-[4/3] rounded-xl overflow-hidden cursor-pointer bg-neutral-900 border transition-all duration-300 ${
                      isActive
                        ? 'border-[#deecd4] ring-2 ring-[#deecd4] shadow-lg'
                        : 'border-white/15 hover:border-white/40 hover:scale-[1.02]'
                    }`}
                  >
                    <img
                      src={imageUrl(imgUrl, 'width=450,quality=80,format=auto')}
                      alt={`${album.title} - ${idx + 1}`}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2.5">
                      <span className="text-xs font-semibold text-white">
                        Billede {idx + 1}
                      </span>
                    </div>
                    <span className="absolute top-2 left-2 rounded-md bg-black/60 px-1.5 py-0.5 text-[10px] font-bold text-[#deecd4] border border-white/10 backdrop-blur-xs">
                      #{idx + 1}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* 3. FOOTER: HORISONTAL SCROLLBAR MED ALLE BILLEDER I ALBUMMET + ALBUM SKIFT */}
      <footer
        className="relative z-50 border-t border-white/10 bg-black/70 px-4 py-3 sm:px-6 sm:py-3.5 backdrop-blur-md flex flex-col gap-2.5"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Scrollbar med miniaturer hvis der er mere end 1 billede */}
        {totalImages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <div
              ref={thumbnailsRef}
              className="max-w-full overflow-x-auto flex items-center justify-start sm:justify-center gap-2 sm:gap-3 py-1 scrollbar-none scroll-smooth mx-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            >
              {images.map((imgUrl, idx) => {
                const isActive = idx === activeIdx;
                return (
                  <button
                    key={`${imgUrl}-thumb-${idx}`}
                    ref={isActive ? activeThumbRef : null}
                    onClick={() => {
                      setActiveIdx(idx);
                      if (viewMode !== 'carousel') setViewMode('carousel');
                    }}
                    className={`relative h-14 w-14 sm:h-18 sm:w-20 shrink-0 rounded-xl overflow-hidden border-2 transition-all duration-300 cursor-pointer ${
                      isActive
                        ? 'border-[#deecd4] ring-2 ring-[#deecd4]/50 scale-105 opacity-100 shadow-lg'
                        : 'border-white/20 opacity-50 hover:opacity-100 hover:border-white/50'
                    }`}
                    aria-label={`Gå til billede ${idx + 1}`}
                  >
                    <img
                      src={imageUrl(imgUrl, 'width=160,height=160,fit=cover,quality=80,format=auto')}
                      alt=""
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                    <span className="absolute bottom-1 right-1 rounded bg-black/70 px-1 text-[9px] font-bold text-white leading-none">
                      {idx + 1}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Bundnavigation til forrige / næste album */}
        {(onPrevAlbum || onNextAlbum) && (
          <div className="flex items-center justify-between text-xs text-gray-400 pt-1 border-t border-white/10">
            <div>
              {onPrevAlbum && (
                <button
                  onClick={onPrevAlbum}
                  className="inline-flex items-center gap-1.5 text-gray-300 hover:text-[#deecd4] transition-colors cursor-pointer py-1"
                >
                  <i className="fa-solid fa-arrow-left text-[10px]" />
                  <span>Forrige album{prevAlbumTitle ? `: ${prevAlbumTitle}` : ''}</span>
                </button>
              )}
            </div>

            <div className="text-[11px] text-gray-400 hidden sm:block">
              Tastaturnavigation: Brug piletaster <span className="text-white">←</span> <span className="text-white">→</span> for at skifte billede
            </div>

            <div>
              {onNextAlbum && (
                <button
                  onClick={onNextAlbum}
                  className="inline-flex items-center gap-1.5 text-gray-300 hover:text-[#deecd4] transition-colors cursor-pointer py-1"
                >
                  <span>Næste album{nextAlbumTitle ? `: ${nextAlbumTitle}` : ''}</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" />
                </button>
              )}
            </div>
          </div>
        )}
      </footer>
    </div>,
    document.body
  );
}
